import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import FacultyUserLink from './pages/LinkFaculty';
import Home from './pages/Home';
import OAuthRedirectHandler from './pages/OAuthRedirectHandler';
import NotFound from './pages/NotFound';
import FeedbackModal from './components/modals/FeedbackModal';
import ProfileSettingsModal from './components/modals/ProfileSettingsModal';
import { AppBar, Toolbar, Typography, Box, GlobalStyles, Tooltip, Button, CircularProgress } from '@mui/material';
import { Settings } from 'react-feather';
import ErrorBoundary from './components/ErrorBoundary';
import { useAnalytics } from './hooks/useAnalytics';
import analyticsService from './services/analytics';

// Setup axios interceptor only once
// Using a flag to prevent duplicate interceptor registration
if (!window._axiosInterceptorRegistered) {
  window._axiosInterceptorRegistered = true;
  
  // Set up global axios interceptor for error tracking
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response) {
        const { status, statusText, config } = error.response;
        
        // Only track server errors (5XX), not client errors (4XX)
        if (status >= 500) {
          analyticsService.trackError(`HTTP ${status} ${statusText}`, {
            url: config.url,
            method: config.method,
            path: new URL(config.url).pathname,
            errorType: 'HTTP_5XX',
            statusCode: status
          });
        }
      } else if (error.request) {
        // The request was made but no response was received
        analyticsService.trackError('Network Error', {
          url: error.config?.url,
          method: error.config?.method,
          errorType: 'NETWORK_ERROR'
        });
      }
      
      // Pass the error along to the next handler
      return Promise.reject(error);
    }
  );
}

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Analytics hook for tracking events
  const { trackLogout } = useAnalytics();

  // Persist the current tab (with a default of 4)
  const [currentTab, setCurrentTab] = useState(() => {
    const savedTab = localStorage.getItem('currentTab');
    return savedTab !== null ? Number(savedTab) : 4;
  });

  // Inactivity timer to automatically log out idle users
  useEffect(() => {
    if (isLoggedIn) {
      let inactivityTimer;
      const inactivityTimeout = 30 * 60 * 1000; // 30 minutes
      
      const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
          console.log('User inactive, logging out...');
          handleLogout();
        }, inactivityTimeout);
      };
      
      // Start the timer when user logs in
      resetInactivityTimer();
      
      // Reset the timer on user activity
      const userActivityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
      userActivityEvents.forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });
      
      // Clean up event listeners on unmount
      return () => {
        clearTimeout(inactivityTimer);
        userActivityEvents.forEach(event => {
          window.removeEventListener(event, resetInactivityTimer);
        });
      };
    }
  }, [isLoggedIn]);

  // Session timer to check token validity periodically
  useEffect(() => {
    if (isLoggedIn) {
      // Check auth status every 2 minutes
      const interval = setInterval(() => {
        axios
          .get(`${import.meta.env.VITE_API_URL}/users/me/`, { withCredentials: true })
          .catch(error => {
            // If we get 401/403, log the user out
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
              console.log('Session expired, logging out...');
              handleLogout();
            }
          });
      }, 120000); // 2 minutes

      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    // Fetch current user info
    axios
      .get(`${import.meta.env.VITE_API_URL}/users/me/`, { withCredentials: true })
      .then(response => {
        setIsLoggedIn(true);
        setCurrentUser(response.data);
      })
      .catch(error => {
        setIsLoggedIn(false);
        setCurrentUser(null);
      })
      .finally(() => {
        setCheckingAuth(false);
      });
  }, []);

  const handleLogout = () => {
    // Track the logout event
    trackLogout();
    
    // Clear tokens from localStorage
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    
    axios
      .post(`${import.meta.env.VITE_API_URL}/users/logout/`, {}, { withCredentials: true })
      .then(() => {
        setIsLoggedIn(false);
        setCurrentUser(null);
      })
      .catch(() => {
        setIsLoggedIn(false);
        setCurrentUser(null);
      });
  };

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
    localStorage.setItem('currentTab', newTab);
  };

  if (checkingAuth) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Use the first name from currentUser (or fallback to "User")
  const userName = currentUser ? currentUser.first_name : 'User';

  return (
    <Router>
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            fontFamily: 'Inter, sans-serif',
          },
          '#root': {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'white',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: '3rem', sm: '3.5rem', md: '4rem' } }}>
            <Box
              component="img"
              src="/logo.png"
              alt="A stylized black graduation cap with white printed circuit board (PCB) patterns replacing the traditional fabric texture. The design symbolizes the intersection of technology and education."
              sx={{
                height: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                maxHeight: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                marginRight: { xs: 1, sm: 1.5, md: 2 },
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                fontSize: { xs: '0.85rem', sm: '1rem', md: '1.25rem' },
              }}
            >
              UA Faculty Matching System
            </Typography>
            {isLoggedIn && (
              <>
                <Tooltip title="Profile Settings">
                  <Button
                    variant="outlined"
                    onClick={() => setProfileModalOpen(true)}
                    startIcon={<Settings size={18} />}
                    sx={{
                      textTransform: 'none',
                      mr: { xs: 0.5, sm: 1, md: 2 },
                      borderColor: 'text.secondary',
                      color: 'text.secondary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                      py: { xs: 0.5, sm: 0.75, md: 1 },
                      px: { xs: 1, sm: 1.25, md: 1.5 },
                      '& .MuiButton-startIcon': {
                        marginRight: { xs: '0.25rem', sm: '0.5rem' },
                      },
                    }}
                  >
                    {userName}
                  </Button>
                </Tooltip>
                <Button
                  variant="text"
                  onClick={handleLogout}
                  sx={{
                    cursor: 'pointer',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                    py: { xs: 0.5, sm: 0.75, md: 1 },
                    px: { xs: 0.75, sm: 1, md: 1.5 },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  Logout
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>

        {/* Spacer for fixed AppBar */}
        <Toolbar />

        <Box
          component="main"
          sx={{
            display: 'flex',
            flexGrow: 1,
            backgroundImage:
              'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/login" />} />
            <Route
              path="/login"
              element={
                !isLoggedIn ? (
                  <Login onLogin={() => setIsLoggedIn(true)} />
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            <Route
              path="/home"
              element={
                isLoggedIn ? (
                  <Home currentTab={currentTab} setCurrentTab={handleTabChange} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/register/link-to-faculty"
              element={
                !isLoggedIn ? (
                  <Login onLogin={() => setIsLoggedIn(true)} />
                ) : currentUser && currentUser.faculty ? (
                  <Navigate to="/home" replace />
                ) : (
                  <FacultyUserLink onLinkSuccess={setCurrentUser} />
                )
              }
            />
            <Route path="/oauth/callback" element={<OAuthRedirectHandler />} />
            {/* Catch-all route for undefined URLs */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>

        {/* Feedback Modal */}
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
        />

        {/* Profile Settings Modal */}
        {currentUser && (
          <ProfileSettingsModal
            open={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
            user={currentUser}
          />
        )}

        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 'auto',
            backgroundColor: 'white',
            color: 'text.secondary',
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography
            component="button"
            variant="body2"
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              background: 'none',
              border: 'none',
              padding: { xs: '0.4rem', sm: '0.5rem', md: '0.5rem' },
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
              textDecoration: 'underline',
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
            }}
            onClick={() => setFeedbackModalOpen(true)}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setFeedbackModalOpen(true);
              }
            }}
          >
            Report a Bug / Submit Feedback
          </Typography>
        </Box>
      </Box>
    </Router>
  );
}

// Main App component with ErrorBoundary
function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

export default App;
